/**
 * TOSS Web Application - Routes Constants
 *
 * Route definitions and navigation configuration.
 * Based on PROJECT_DOCUMENTATION.md Section 4 and 22.
 */

import { UserRole } from '@/types';
import { hasPermissionKey } from '@/store/permissionStore';
import { PK } from './permissions';

// ============================================================================
// Route Paths
// ============================================================================

/**
 * Application route paths
 */
export const ROUTES = {
  // Auth routes (public)
  auth: {
    login: '/login',
    register: '/register',
    forgotPassword: '/forgot-password',
    resetPassword: '/reset-password',
  },

  // Main routes
  home: '/',
  dashboard: '/dashboard',

  // Applications module
  applications: {
    list: '/applications',
    details: '/applications/:id',
    create: '/applications/new',
    edit: '/applications/:id/edit',
    // `inspection` (legacy per-item/bulk flow) and `works` (same page, different entry) were
    // removed with `InspectionPage.tsx` — inspection redesign, Phase 4. The one inspection
    // surface is `mechanicInspection`.
    mechanicInspection: '/applications/:id/pto/:ptoId/mechanic-inspection',
    addExpense: '/applications/:id/pto/:ptoId/add-expense',
    /** One version of an application: what changed, the difference from now, the whole state. */
    version: '/applications/:id/versions/:versionId',
  },

  // PTO module (for Accountant and HeadOfFMO)
  pto: {
    list: '/pto',
    details: '/pto/:id',
    // `inspectionEdit` was removed with `InspectionEditPage.tsx` (inspection redesign, Phase 4)
    // and restored 22.09.2026 at the SAME address, now rendering `FmoInspectionReviewPage`:
    // ФМО's own editor layout over the flow's endpoints, not a second write contract. Keeping
    // the old path means bookmarks and the expenses tab's `returnTo` never broke.
    inspectionEdit: '/pto/:id/inspection/edit',
    verification: '/pto/:id/verification',
    payment: '/pto/:id/payment',
  },

  // Tasks module
  tasks: {
    list: '/tasks',
    details: '/tasks/:id',
    create: '/tasks/new',
    edit: '/tasks/:id/edit',
    report: '/tasks/:id/report',
  },

  // Expenses module
  expenses: {
    list: '/expenses',
    all: '/expenses/all',
    details: '/expenses/:id',
    create: '/expenses/new',
    edit: '/expenses/:id/edit',
  },

  // Workers module
  workers: {
    list: '/workers',
    details: '/workers/:id',
    create: '/workers/new',
    edit: '/workers/:id/edit',
    deletionRequests: '/workers/deletion-requests',
  },

  // Directory modules
  directory: {
    index: '/directory',
    customers: {
      list: '/directory/customers',
      details: '/directory/customers/:id',
      create: '/directory/customers/new',
      edit: '/directory/customers/:id/edit',
    },
    containers: {
      list: '/directory/containers',
      details: '/directory/containers/:id',
      create: '/directory/containers/new',
      edit: '/directory/containers/:id/edit',
      history: '/directory/containers/:id/history',
    },
    equipment: {
      list: '/directory/equipment',
      details: '/directory/equipment/:id',
      create: '/directory/equipment/new',
      edit: '/directory/equipment/:id/edit',
      history: '/directory/equipment/:id/history',
    },
    services: {
      list: '/directory/services',
      details: '/directory/services/:id',
      create: '/directory/services/new',
      edit: '/directory/services/:id/edit',
    },
    pto: {
      list: '/directory/pto',
    },
    directions: {
      list: '/directory/directions',
    },
    materials: {
      list: '/directory/materials',
    },
    // Vision v4 Phase 1
    stations: {
      list: '/directory/stations',
    },
    terminals: {
      list: '/directory/terminals',
    },
    // Vision v4 Phase 2
    pricing: {
      list: '/directory/pricing',
    },
    // Inspection redesign, Phase 2 — replace the hardcoded container type/size enums
    containerAttributes: {
      list: '/directory/container-attributes',
    },
    inspectionReasons: {
      list: '/directory/inspection-reasons',
    },
    // PTO crew, Phase 4
    mechanicTypes: {
      list: '/directory/mechanic-types',
    },
  },

  // Instructions module
  instructions: {
    list: '/instructions',
    details: '/instructions/:id',
    create: '/instructions/new',
    edit: '/instructions/:id/edit',
  },

  // Messenger
  messenger: {
    list: '/messenger',
    chat: '/messenger/:chatId',
  },

  // Notifications
  notifications: {
    list: '/notifications',
  },

  // Profile
  profile: {
    view: '/profile',
    edit: '/profile/edit',
    settings: '/profile/settings',
  },

  // FMO module
  fmo: {
    list: '/fmo',
    details: '/fmo/:id',
    verification: '/fmo/:id/verify',
    documents: '/fmo/:id/documents',
    payments: '/fmo/payments',
  },

  // Payroll module (Vision v4 Phase 5 Block 11)
  payroll: {
    summary: '/payroll',
  },

  // Admin module (Administrator/SuperAdmin only)
  admin: {
    permissions: '/admin/permissions',
    auditLogs: '/admin/audit-logs',
    legalDocuments: '/admin/legal-documents',
    documentSettings: '/admin/document-settings',
    tableConfig: '/admin/tables',
    emailGateway: '/admin/email-gateway',
  },

  // Errors
  errors: {
    notFound: '/404',
    forbidden: '/403',
    serverError: '/500',
  },

  // ==========================================================================
  // Client portal (UserRole.Client) — the external customer audience.
  //
  // EVERY client screen lives under `/client/`, the auth screens included
  // (decision Р16). That is not cosmetic: release Р2 ships a second installable
  // entry point whose manifest declares `scope: '/client/'`, and a login page
  // outside that scope throws the customer out of the installed app the moment
  // their session ends. Moving these paths later means broken bookmarks and
  // broken links in already-sent emails.
  //
  // `dashboard` is `/client` itself — the bare path, with NO trailing slash. Release Р2
  // put the real dashboard behind it (counters, recent applications, documents and
  // notifications) and two other things depend on that exact string: the portal's own
  // web app manifest declares `id`/`scope`/`start_url` as `'/client'`, and
  // `getDefaultRoute(UserRole.Client)` below returns it verbatim. `'/client/'` would not
  // cover `'/client'`, so do not "normalise" it.
  // ==========================================================================
  client: {
    login: '/client/login',
    forgotPassword: '/client/forgot-password',
    resetPassword: '/client/reset-password',
    dashboard: '/client',
    applications: {
      list: '/client/applications',
      create: '/client/applications/new',
      details: '/client/applications/:id',
      /**
       * Правка заявки, которую диспетчер ещё не принял (31.08.2026).
       *
       * Под `/client/applications`, в отличие от формы заготовки: здесь это ПРАВИЛЬНЫЙ заголовок
       * — `ClientHeader` титулует страницу по первому совпадению префикса, и «Заявки» ровно то,
       * чем правка заявки и является. Объявлен ПОСЛЕ `details` и до пути не конфликтует с ним:
       * `/:id` и `/:id/edit` — разные шаблоны, но порядок в таблице маршрутов всё равно значим,
       * как у `templateNew` перед `templateEdit`.
       */
      edit: '/client/applications/:id/edit',
    },
    // NOT `/client/applications/templates` — see `clientApplicationFromTemplatePath` below
    // for why a path under `/client/applications` would misfire `ClientHeader`'s title.
    templates: '/client/templates',
    // Форма заготовки — на обеих ширинах страница по этим адресам (27.08.2026): диалога на
    // компьютере больше нет, потому что формы шаблона больше нет вовсе — это форма ЗАЯВКИ в
    // режиме шаблона, см. `ClientTemplateFormPage`.
    templateNew: '/client/templates/new',
    templateEdit: '/client/templates/:id/edit',
    documents: '/client/documents',
    // Release Р4 (r4-fleet-is-scoped-to-our-own-service-history.md §6): «мой парк» and «ПТО,
    // с которыми работаем». `containers.details` uses the same `:id` param name as every
    // other portal detail route, so `generatePath` stays a one-line call everywhere.
    containers: {
      list: '/client/containers',
      details: '/client/containers/:id',
    },
    ptos: '/client/ptos',
    messenger: '/client/messenger',
    // One conversation. A child of the list on purpose: `isClientNavItemActive` matches on a
    // segment boundary, so «Сообщения» stays lit while a chat is open — no extra flag, no second
    // entry in the menu config.
    messengerChat: '/client/messenger/:chatId',
    notifications: '/client/notifications',
    profile: '/client/profile',
    // Phone-only sub-screens of «Профиль» (mobile rework, 2026-08-21). `/client/profile`
    // became a HUB on the phone — an identity block plus a menu — and the three read-only
    // blocks it used to stack under each other moved one level down, each with its own
    // address, its own back arrow and its own place in history.
    //
    // On ≥768 these paths redirect to `/client/profile`, which still carries all three as
    // cards: the desktop screen was not touched by the rework, so a link opened there must
    // land on the screen that actually shows the block rather than on a phone layout
    // stretched across a 1280 px window.
    //
    // Children of `/client/profile` on purpose: `isClientNavItemActive` matches a prefix on
    // a segment boundary, so «Профиль» stays lit in the bottom bar while a customer reads
    // any of them — no extra flag, no second entry in the menu config.
    profilePersonal: '/client/profile/personal',
    profileCompany: '/client/profile/company',
    profileColleagues: '/client/profile/colleagues',
    // The one WRITE of the profile branch. A screen on the phone (a dialog with three
    // password fields under a soft keyboard is a box scrolling inside a box); still the
    // dialog on ≥768, where this path redirects to `/client/profile`.
    profilePassword: '/client/profile/password',

    // The company section — people and their rights. NOT under `/client/profile`: those paths are
    // phone-only sub-screens that redirect to the profile at ≥768, and this is a full section on
    // both surfaces. `employeeRoles` is a CHILD path so `isClientNavItemActive`, which matches a
    // prefix on a segment boundary, keeps «Сотрудники» lit while the roles screen is open.
    employees: '/client/employees',
    employeeRoles: '/client/employees/roles',
  },
} as const;

// ============================================================================
// Route Helpers
// ============================================================================

/**
 * Generate route with parameters
 */
export function generatePath(
  path: string,
  params: Record<string, string | number>
): string {
  let result = path;
  Object.entries(params).forEach(([key, value]) => {
    result = result.replace(`:${key}`, String(value));
  });
  return result;
}

/**
 * Application details route
 */
export function applicationDetailsPath(id: string): string {
  return generatePath(ROUTES.applications.details, { id });
}

/**
 * Client-portal application details route.
 *
 * Separate from {@link applicationDetailsPath} on purpose: the two ids look alike but
 * lead to different audiences' screens (`/applications/:id` renders the internal card
 * with mechanics and money, `/client/applications/:id` the customer's read-only one).
 */
export function clientApplicationDetailsPath(id: string): string {
  return generatePath(ROUTES.client.applications.details, { id });
}

/**
 * Client-portal application EDIT route (31.08.2026).
 *
 * Reachable only while the application is still «Новая» — the card hides the action once a
 * dispatcher accepts it, and both the form's resolver and the server refuse afterwards. The helper
 * itself carries no such check: it builds a path, and «можно ли» is a question about an
 * application, not about a string.
 */
export function clientApplicationEditPath(id: string): string {
  return generatePath(ROUTES.client.applications.edit, { id });
}

/**
 * Client-portal container card route (release Р4).
 *
 * Same reasoning as {@link clientApplicationDetailsPath}: kept separate from any internal
 * container route so the two audiences' cards can never be confused by a shared helper.
 */
export function clientContainerDetailsPath(id: string): string {
  return generatePath(ROUTES.client.containers.details, { id });
}

/**
 * Query param the create page reads to prefill itself from a saved template (release Р5).
 *
 * Sibling of `CLIENT_APPLICATION_COPY_FROM_PARAM` (`clientApplicationCopy.ts`), which lives
 * outside `constants/routes.ts` only because the frontend task that built «Копировать
 * заявку» had `features/client/**` as its sole file scope — see that file's own docblock.
 * This one has no such constraint, so it is declared where the contract's own seam table
 * (r5-application-templates.md §7) puts it.
 */
export const CLIENT_APPLICATION_TEMPLATE_ID_PARAM = 'templateId';

/**
 * `/client/applications/new?templateId={id}` — navigates to the create form and prefills it
 * from a template's full card. Pure navigation, never a mutation (contract §4.5: "applying a
 * template creates nothing" — the customer still submits through the normal
 * `POST /api/client/applications`).
 */
export function clientApplicationFromTemplatePath(id: string): string {
  return `${ROUTES.client.applications.create}?${CLIENT_APPLICATION_TEMPLATE_ID_PARAM}=${encodeURIComponent(id)}`;
}

/**
 * Query-parameter names of the portal's applications list.
 *
 * Release Р2 gave the list two NEW url params, `status` and `search`, so the dashboard's
 * counter cards and its search quick action have something to hand off to. They are a
 * hand-off, not a second filter store: `ClientApplicationsPage` seeds its state from them
 * once on mount and then removes them, after which the list's own persistence (`cf` /
 * `sort` / `dir` from `usePersistentTableFilters`, plus localStorage) is the single
 * representation of the filter — exactly as it was before Р2.
 *
 * Declared here rather than in either screen so the writer (dashboard) and the reader
 * (list) cannot disagree about a spelling; both import this object.
 */
export const CLIENT_APPLICATIONS_QUERY_PARAMS = {
  /** One `ClientApplicationStatus` value, e.g. `Cancelled`. */
  status: 'status',
  /** Free-text search, pre-filled into the list's search box. */
  search: 'search',
} as const;

/**
 * Client-portal applications list, optionally pre-filtered.
 *
 * @example
 * ```ts
 * clientApplicationsListPath({ status: 'Cancelled' }); // '/client/applications?status=Cancelled'
 * clientApplicationsListPath({ search: 'ABCU 123' });  // '/client/applications?search=ABCU+123'
 * ```
 */
export function clientApplicationsListPath(options?: {
  status?: string;
  search?: string;
}): string {
  const params = new URLSearchParams();
  if (options?.status) params.set(CLIENT_APPLICATIONS_QUERY_PARAMS.status, options.status);
  const search = options?.search?.trim();
  if (search) params.set(CLIENT_APPLICATIONS_QUERY_PARAMS.search, search);

  const query = params.toString();
  return query
    ? `${ROUTES.client.applications.list}?${query}`
    : ROUTES.client.applications.list;
}

/**
 * Application edit route
 */
export function applicationEditPath(id: string): string {
  return generatePath(ROUTES.applications.edit, { id });
}

/**
 * Application version route. `versionId` is the server's `long` — the largest history row id of
 * the version's last transaction — so it travels as a number, never as a guid.
 */
export function applicationVersionPath(id: string, versionId: number): string {
  return generatePath(ROUTES.applications.version, { id, versionId });
}

/**
 * Mechanic inspection route (extended inspection page for mechanics)
 */
export function mechanicInspectionPath(id: string, ptoId: string): string {
  return generatePath(ROUTES.applications.mechanicInspection, { id, ptoId });
}

/**
 * Add expense route (separate page for adding expenses to PTO inspection)
 */
export function addExpensePath(id: string, ptoId: string): string {
  return generatePath(ROUTES.applications.addExpense, { id, ptoId });
}

/**
 * Task details route
 */
export function taskDetailsPath(id: string): string {
  return generatePath(ROUTES.tasks.details, { id });
}

/**
 * Task edit route
 */
export function taskEditPath(id: string): string {
  return generatePath(ROUTES.tasks.edit, { id });
}

/**
 * Expense details route
 */
export function expenseDetailsPath(id: string): string {
  return generatePath(ROUTES.expenses.details, { id });
}

/**
 * Worker details route
 */
export function workerDetailsPath(id: string): string {
  return generatePath(ROUTES.workers.details, { id });
}

/**
 * Worker edit route
 */
export function workerEditPath(id: string): string {
  return generatePath(ROUTES.workers.edit, { id });
}

/**
 * Customer details route
 */
export function customerDetailsPath(id: string): string {
  return generatePath(ROUTES.directory.customers.details, { id });
}

/**
 * Container details route
 */
export function containerDetailsPath(id: string): string {
  return generatePath(ROUTES.directory.containers.details, { id });
}

/**
 * Equipment details route
 */
export function equipmentDetailsPath(id: string): string {
  return generatePath(ROUTES.directory.equipment.details, { id });
}

/**
 * Instruction details route
 */
export function instructionDetailsPath(id: string): string {
  return generatePath(ROUTES.instructions.details, { id });
}

/**
 * Messenger chat route
 */
/** `/client/messenger/{chatId}` — one conversation in the customer's cabinet. */
export function clientChatPath(chatId: string): string {
  return generatePath(ROUTES.client.messengerChat, { chatId });
}

export function messengerChatPath(chatId: string): string {
  return generatePath(ROUTES.messenger.chat, { chatId });
}

/**
 * FMO details route
 */
export function ptoInspectionEditPath(id: string): string {
  return generatePath(ROUTES.pto.inspectionEdit, { id });
}

export function fmoDetailsPath(id: string): string {
  return generatePath(ROUTES.fmo.details, { id });
}

/**
 * FMO verification route
 */
export function fmoVerificationPath(id: string): string {
  return generatePath(ROUTES.fmo.verification, { id });
}

/**
 * FMO documents route
 */
export function fmoDocumentsPath(id: string): string {
  return generatePath(ROUTES.fmo.documents, { id });
}

/**
 * PTO details route
 */
export function ptoDetailsPath(id: string): string {
  return generatePath(ROUTES.pto.details, { id });
}

// ============================================================================
// Route Configuration
// ============================================================================

/**
 * Public routes that don't require authentication
 */
export const PUBLIC_ROUTES: string[] = [
  ROUTES.auth.login,
  ROUTES.auth.register,
  ROUTES.auth.forgotPassword,
  ROUTES.auth.resetPassword,
  // Client portal — the three anonymous screens only. `/client` itself is NOT here:
  // `isPublicRoute()` matches by prefix, so listing it would declare the whole portal
  // public.
  ROUTES.client.login,
  ROUTES.client.forgotPassword,
  ROUTES.client.resetPassword,
];

/**
 * Check if a route is public
 */
export function isPublicRoute(path: string): boolean {
  return PUBLIC_ROUTES.some(
    (publicPath) => path === publicPath || path.startsWith(publicPath)
  );
}

/**
 * Default route after login. Reads the user's granted keys directly so role
 * matters only as a fallback when the permission store hasn't populated yet
 * (e.g. SSR-style first paint, which we don't ship — kept defensively).
 *
 * Decision tree:
 *   0. Client role → /client, the portal root (which itself redirects to the
 *      applications list). Handled first and explicitly: the client audience never
 *      receives permission keys at all (decision Р3/Р31), so falling through to the
 *      key logic below would land a customer on /profile — an internal screen
 *      InternalOnlyRoute then refuses. This is also the path an installed PWA takes:
 *      `start_url: '/'` → `HomeRedirect` → here.
 *   1. Has FMO ПТО tab key? → /pto (the FMO-style landing).
 *   2. Has Applications view key? → /applications.
 *   3. Has any tasks view key? → /tasks.
 *   4. Otherwise → /profile.
 */
export function getDefaultRoute(role: UserRole): string {
  if (role === UserRole.Client) {
    return ROUTES.client.dashboard;
  }

  if (hasPermissionKey(PK.Fmo.PtoView) && !hasPermissionKey(PK.Applications.Edit)) {
    return ROUTES.pto.list;
  }

  if (hasPermissionKey(PK.Applications.View)) {
    return ROUTES.applications.list;
  }

  if (hasPermissionKey(PK.Tasks.ViewOwn) || hasPermissionKey(PK.Tasks.ViewAll)) {
    return ROUTES.tasks.list;
  }

  return ROUTES.profile.view;
}

// ============================================================================
// Breadcrumb Configuration
// ============================================================================

/**
 * Breadcrumb item
 */
export interface BreadcrumbItem {
  label: string;
  path?: string;
}

/**
 * Route breadcrumb configuration
 */
export const ROUTE_BREADCRUMBS: Record<string, BreadcrumbItem[]> = {
  [ROUTES.applications.list]: [{ label: 'Заявки' }],
  [ROUTES.applications.create]: [
    { label: 'Заявки', path: ROUTES.applications.list },
    { label: 'Создание' },
  ],
  [ROUTES.tasks.list]: [{ label: 'Задачи' }],
  [ROUTES.tasks.create]: [
    { label: 'Задачи', path: ROUTES.tasks.list },
    { label: 'Создание' },
  ],
  [ROUTES.expenses.list]: [{ label: 'Расходы' }],
  [ROUTES.expenses.all]: [{ label: 'Все расходы' }],
  [ROUTES.expenses.create]: [
    { label: 'Расходы', path: ROUTES.expenses.list },
    { label: 'Создание' },
  ],
  [ROUTES.workers.list]: [{ label: 'Сотрудники' }],
  [ROUTES.workers.create]: [
    { label: 'Сотрудники', path: ROUTES.workers.list },
    { label: 'Создание' },
  ],
  [ROUTES.directory.customers.list]: [
    { label: 'Справочники' },
    { label: 'Клиенты' },
  ],
  [ROUTES.directory.containers.list]: [
    { label: 'Справочники' },
    { label: 'Контейнеры' },
  ],
  [ROUTES.directory.equipment.list]: [
    { label: 'Справочники' },
    { label: 'Энергетическая установка' },
  ],
  [ROUTES.directory.services.list]: [
    { label: 'Справочники' },
    { label: 'Услуги' },
  ],
  [ROUTES.directory.pto.list]: [{ label: 'Справочники' }, { label: 'ПТО' }],
  [ROUTES.directory.directions.list]: [
    { label: 'Справочники' },
    { label: 'Направления' },
  ],
  [ROUTES.directory.materials.list]: [
    { label: 'Справочники' },
    { label: 'Материалы' },
  ],
  [ROUTES.directory.stations.list]: [
    { label: 'Справочники' },
    { label: 'Станции' },
  ],
  [ROUTES.directory.terminals.list]: [
    { label: 'Справочники' },
    { label: 'Терминалы' },
  ],
  [ROUTES.directory.pricing.list]: [
    { label: 'Справочники' },
    { label: 'Прайс-лист' },
  ],
  [ROUTES.directory.containerAttributes.list]: [
    { label: 'Справочники' },
    { label: 'Типы и размеры контейнеров' },
  ],
  [ROUTES.directory.inspectionReasons.list]: [
    { label: 'Справочники' },
    { label: 'Причины осмотра' },
  ],
  [ROUTES.directory.mechanicTypes.list]: [
    { label: 'Справочники' },
    { label: 'Типы исполнителей' },
  ],
  [ROUTES.instructions.list]: [{ label: 'Инструкции' }],
  [ROUTES.profile.view]: [{ label: 'Профиль' }],
  [ROUTES.notifications.list]: [{ label: 'Уведомления' }],
};

/**
 * Get breadcrumbs for a route
 */
export function getBreadcrumbs(path: string): BreadcrumbItem[] {
  return ROUTE_BREADCRUMBS[path] ?? [];
}
